import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface FinancialData {
  id: string;
  title: string;
  source: string;
  date: string;
  summary: string;
  url: string;
  impact: "positive" | "negative" | "neutral";
  metric?: string;
  value?: string;
}

// Fallback mock data in case the API fails
export const MOCK_FINANCIAL_DATA: FinancialData[] = [
  {
    id: '1',
    title: 'Quarterly Earnings Report',
    source: 'Company Investor Relations',
    date: '2025-01-15',
    summary: 'The company reported Q4 earnings above analyst expectations with revenue growth of 18% year-over-year.',
    url: 'https://example.com/financial/1',
    impact: 'positive',
    metric: 'Revenue Growth',
    value: '+18%'
  },
  {
    id: '2',
    title: 'Debt Restructuring Announcement',
    source: 'Financial Times',
    date: '2025-02-03',
    summary: 'The company has successfully restructured its long-term debt, improving liquidity position and reducing interest expenses.',
    url: 'https://example.com/financial/2',
    impact: 'positive',
    metric: 'Debt-to-Equity Ratio',
    value: '0.75'
  },
  {
    id: '3',
    title: 'New Investment Round',
    source: 'TechCrunch',
    date: '2025-02-28',
    summary: 'The company secured $50 million in Series C funding to accelerate growth initiatives and product development.',
    url: 'https://example.com/financial/3',
    impact: 'positive',
    metric: 'Cash Reserves',
    value: '$112M'
  },
  {
    id: '4',
    title: 'Cost-Cutting Measures',
    source: 'Bloomberg',
    date: '2025-03-10',
    summary: 'The company announced a cost reduction program aimed at improving operational efficiency.',
    url: 'https://example.com/financial/4',
    impact: 'neutral',
    metric: 'Operating Expenses',
    value: '-8%'
  },
  {
    id: '5',
    title: 'Profit Warning Issued',
    source: 'Reuters',
    date: '2025-02-15',
    summary: 'The company issued a profit warning for the upcoming quarter due to increased competition and rising input costs.',
    url: 'https://example.com/financial/5',
    impact: 'negative',
    metric: 'Profit Margin',
    value: '4.2%'
  }
];

// Funding specific mock data
export const MOCK_FUNDING_DATA: FinancialData[] = [
  {
    id: 'f1',
    title: 'Series D Funding Round',
    source: 'TechCrunch',
    date: '2025-01-10',
    summary: 'The company raised $120 million in Series D funding led by Sequoia Capital, at a $2.4 billion valuation.',
    url: 'https://example.com/funding/1',
    impact: 'positive',
    metric: 'Valuation',
    value: '$2.4B'
  },
  {
    id: 'f2',
    title: 'Strategic Investment',
    source: 'Bloomberg',
    date: '2024-11-15',
    summary: 'The company secured a $35 million strategic investment from Microsoft to expand its enterprise offerings.',
    url: 'https://example.com/funding/2',
    impact: 'positive',
    metric: 'Investment Amount',
    value: '$35M'
  },
  {
    id: 'f3',
    title: 'Acquisition of Startup',
    source: 'Reuters',
    date: '2024-10-05',
    summary: 'The company acquired an AI technology startup for $28 million to enhance its product capabilities.',
    url: 'https://example.com/funding/3',
    impact: 'neutral',
    metric: 'Acquisition Cost',
    value: '$28M'
  }
];

export const useFinancialReporting = (companyName: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [financialData, setFinancialData] = useState<FinancialData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Function to fetch financial data from the edge function
  const fetchFinancialData = async (category = 'all') => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('financial-reporting', {
        body: { 
          companyName: companyName,
          category: category
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data.financialData && Array.isArray(data.financialData)) {
        setFinancialData(data.financialData);
      } else {
        throw new Error("Invalid response from API");
      }
    } catch (err) {
      console.error("Error fetching financial data:", err);
      setError("Failed to fetch financial data. Using sample data instead.");
      
      // Use appropriate mock data based on category
      if (category === 'funding') {
        setFinancialData(MOCK_FUNDING_DATA);
      } else {
        setFinancialData(MOCK_FINANCIAL_DATA);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    if (companyName) {
      fetchFinancialData(selectedCategory);
    }
  }, [companyName]);
  
  // Handle category change
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    fetchFinancialData(category);
  };
  
  // Handle manual refresh
  const handleRefresh = () => {
    fetchFinancialData(selectedCategory);
  };

  // Display appropriate financial data for the current category
  const displayFinancialData = financialData.length > 0 ? 
    financialData : 
    (selectedCategory === 'funding' ? MOCK_FUNDING_DATA : MOCK_FINANCIAL_DATA);

  return {
    isLoading,
    selectedCategory,
    financialData: displayFinancialData,
    error,
    handleCategoryChange,
    handleRefresh,
    setSelectedCategory
  };
};
